import { PhoenixMetrics } from "@amzn/ads-phoenix-type-script-client";
import type { AssetLocation } from "@amzn/d16-g-client-deck-builder-typescript-client";
import { InsightType } from "@amzn/d16-g-client-deck-builder-typescript-client";
import { useAsync } from "@react-hookz/web";
import type { AsyncState, UseAsyncActions } from "@react-hookz/web";
import { isEmpty, some } from "lodash";
import { useStore } from "zustand";

import { getCdbApiClient } from "../../api/cdbApi/cdbApi";
import { TELEMETRY_ID, PHOENIX_FEATURE } from "../../constants/telemetry";
import { FeatureFlagsStore } from "../../store/featureFlags";
import { InsightLinkageStore } from "../../store/insightLinkage";
import { ACCOUNT_AGNOSTIC_IDS } from "../../types/common";
import { getMarketingContentAndPitchUrl } from "../../utils/downloadUrl.utils";
import { getAdvertiserId } from "../../utils/permission.utils";
import {
    resolveOpportunityId,
    resolveSfObjectId,
    resolveSfObjectType,
} from "../../utils/sfObject.utils";
import {
    getFilterTelemetryDetails,
    getUnifiedFilterTelemetryDetails,
    logWithTakt,
} from "../../utils/telemetry.utils";
import { getInsightIdFromSearchParams } from "../../utils/window.utils";

export interface CreateInsightLinkageAndDownloadProps {
    title: string;
    content: AssetLocation | undefined;
    insightType: InsightType;
    identifier: string;
    downloadUrl: string | undefined;
    description: string;
    contentId?: string;
    contentVersion?: string;
}

export function useCreateInsightLinkageAndDownload(): [
    AsyncState<void>,
    UseAsyncActions<void, [CreateInsightLinkageAndDownloadProps]>,
] {
    const [useUnifiedSearch] = useStore(FeatureFlagsStore, (state) => [
        state.unifiedSearch,
    ]);

    // if advertiser id is null, the download came from account agnostic view
    let advertiserId = getAdvertiserId()?.trim();
    if (!advertiserId) {
        advertiserId = getInsightIdFromSearchParams()
            ? ACCOUNT_AGNOSTIC_IDS.WITH_IDENTIFIER
            : ACCOUNT_AGNOSTIC_IDS.NO_IDENTIFIER;
    }
    const [sfObject, resetInsightLinkage] = useStore(
        InsightLinkageStore,
        (state) => [state.sfObject, state.resetInsightLinkage],
    );

    const [insightLinkageState, insightLinkageAction] = useAsync(
        async (props: CreateInsightLinkageAndDownloadProps) => {
            const {
                title,
                content,
                insightType,
                identifier,
                downloadUrl,
                description,
                contentId,
                contentVersion,
            } = props;

            const { executionId } = await (
                await getCdbApiClient()
            ).createInsightLinkage({
                advertiserId,
                title,
                opportunityId: resolveOpportunityId(sfObject),
                insightType,
                // Should pass content and remove content url when possible to prevent insight linkage errors related to an expired pre-signed url
                // The insight linkage lambda has logic to remove content if contentUrl provided
                contentUrl:
                    isEmpty(content) || some(content, isEmpty)
                        ? downloadUrl
                        : undefined,
                identifier,
                content:
                    insightType === InsightType.MARKETING ? undefined : content,
                description,
                salesforceObjectId: resolveSfObjectId(sfObject),
                salesforceObjectType: resolveSfObjectType(sfObject),
            });
            logWithTakt(TELEMETRY_ID.INSIGHT_LINKAGE_REQUEST, {
                advertiserId,
                executionId,
                opportunityId: resolveOpportunityId(sfObject),
                sfObject,
            });
            PhoenixMetrics.emit(
                TELEMETRY_ID.INSIGHT_LINKAGE_REQUEST,
                PHOENIX_FEATURE.DISCOVERY,
                {
                    customData: {
                        advertiserId,
                        executionId,
                        opportunityId: resolveOpportunityId(sfObject),
                        sfObject,
                    },
                },
            );
            const filters = useUnifiedSearch
                ? getUnifiedFilterTelemetryDetails()
                : getFilterTelemetryDetails();
            logWithTakt(TELEMETRY_ID.INSIGHT_DOWNLOAD, {
                ...filters,
                insightType,
                identifier,
            });
            PhoenixMetrics.emit(
                TELEMETRY_ID.INSIGHT_DOWNLOAD,
                PHOENIX_FEATURE.DISCOVERY,
                {
                    contentInfo: identifier
                        ? {
                              contentProvider: insightType,
                              contentType: "",
                              contentId: identifier,
                          }
                        : undefined,
                    customData: {
                        filters,
                    },
                },
            );

            // For marketing content, call the api to increment content stats
            const finalDownloadUrl = await getMarketingContentAndPitchUrl(
                insightType,
                contentId,
                contentVersion,
                downloadUrl,
            );

            window.open(finalDownloadUrl, "_blank");
            resetInsightLinkage();
        },
    );
    return [insightLinkageState, insightLinkageAction];
}
